import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PATH } from 'src/constants/Path';
import { USER_FEEDBACK_KYB_APPROVED_FIRST_LOGIN } from 'src/constants/UserFeedback/Dashboard/UserFeedback';
import {
  useDomainEnv,
  useEnv,
  useNotificationAnnouncementData,
} from 'src/hooks';
import { useHelpAndDocumentationContext } from 'src/pages/HelpAndDocumentation/hooks/useHelpAndDocumentationContext';
import { RootState } from 'src/redux/reducers/rootReducer';
import { USER_FEEDBACK_ACTION_TYPES } from 'src/redux/types/userFeedbackTypes';
import { dayJs, now } from 'src/utilities/day';
import { storage } from 'src/utilities/Storage';

const LIMIT_DAYS_TO_SHOW_FEEDBACK = 5;

export const useMainLayoutImpl = (pageTitle: string) => {
  const { pathname } = useLocation();
  const [showRightbar, setShowRightbar] = useState(false);
  const { isProdEnv } = useEnv();
  const { isDomainProdEnv } = useDomainEnv();
  const { whatsNewPreview, whatsNewUnread } = useNotificationAnnouncementData();
  const { loadingPaymentInfo, isGeneralLoading, isTopupShow, b2xUser } =
    useSelector((state: RootState) => state.main);
  const { account_type, account_type_last_updated } = b2xUser;

  const { context } = useHelpAndDocumentationContext();
  const dispatch = useDispatch();

  const [neverClickedHelpButton, setNeverClickedHelpButton] = useState<boolean>(
    () => storage.get('neverClickedHelpButton', true) ?? true,
  );

  useEffect(() => {
    storage.set('neverClickedHelpButton', neverClickedHelpButton);
  }, [neverClickedHelpButton]);

  const showWhatsNewButton =
    pathname === PATH.home && (whatsNewPreview?.length ?? 0) > 0;

  const defaultTitle = 'OY! Dashboard';

  // check user's first time login into a browser
  useEffect(() => {
    const hasLogin = storage.get('firstTimeLogin', undefined);
    if (!hasLogin) {
      const firstTimeLoginDate = dayJs().toISOString();
      storage.set('firstTimeLogin', firstTimeLoginDate);
    }
  }, []);

  useEffect(() => {
    if (
      account_type == 'APPROVED' &&
      account_type_last_updated &&
      now().diff(dayJs(account_type_last_updated), 'days') <=
        LIMIT_DAYS_TO_SHOW_FEEDBACK
    ) {
      dispatch({
        type: USER_FEEDBACK_ACTION_TYPES.SHOW_USER_FEEDBACK_MODAL,
        payload: USER_FEEDBACK_KYB_APPROVED_FIRST_LOGIN,
      });
    }
  }, [account_type, account_type_last_updated, dispatch]);

  //set tab title
  useEffect(() => {
    const formattedTitle = pageTitle
      ? `${pageTitle} - ${defaultTitle}`
      : defaultTitle;
    const envTitle = isProdEnv ? formattedTitle : `Demo ${formattedTitle}`;
    const finalTitle = isDomainProdEnv ? envTitle : `[DEV] ${envTitle}`;
    document.title = finalTitle;
  }, [pageTitle, isDomainProdEnv, isProdEnv]);

  const toggleRightbar = () => {
    setShowRightbar(prev => !prev);
    AnalyticsManager.instance().track(AnalyticsEvent.click_something_new);
  };

  const onClickHelp = (
    helpCategoryIndex: number,
    subCategoryPrefix?: string[],
  ) => {
    context.onClickCategory(helpCategoryIndex, subCategoryPrefix);
    if (neverClickedHelpButton) setNeverClickedHelpButton(false);
    AnalyticsManager.instance().track(AnalyticsEvent.click_help_button, {
      from_page:
        pathname === PATH.TRANSACTION_REPORT.accountStatement
          ? 'account_statement'
          : 'va',
    });
  };

  const state = {
    isGeneralLoading,
    isProdEnv,
    loadingPaymentInfo,
    whatsNewPreview,
    showRightbar,
    showWhatsNewButton,
    whatsNewUnread,
    helpAndDocumentationContext: context,
    neverClickedHelpButton,
    isTopupShow,
  };

  return {
    state,
    toggleRightbar,
    onClickHelp,
  };
};
